/**
 * Express Checkout & Instamojo Payment Integration Module for Shopify (Cordsetzone)
 * Handles Customer Delivery Address Collection, Pincode Auto-Lookup (City & State),
 * Form Validation, Instamojo Redirection with Pre-filled URL,
 * and Post-Payment Order Confirmation with Console Logging.
 */

(function () {
  'use strict';

  // API Configuration
  const API_CONFIG = {
    PINCODE_LOOKUP: 'https://api.postalpincode.in/pincode/',
    COUNTRY_CODE: '+91',
    TIMEOUT_MS: 10000
  };

  /**
   * Reusable fetch with timeout & JSON parsing
   */
  async function fetchWithTimeout(url, options = {}, timeoutMs = API_CONFIG.TIMEOUT_MS) {
    const controller = new AbortController();
    const timerId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          ...(options.headers || {})
        }
      });
      clearTimeout(timerId);

      if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      clearTimeout(timerId);
      if (err.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection.');
      }
      throw err;
    }
  }

  /**
   * Main Express Checkout Controller Class
   */
  class OTPBuyNowController {
    constructor(blockId) {
      this.blockId = blockId;
      this.triggerBtn = document.getElementById(`CustomBuyNowBtn-${blockId}`);
      this.backdrop = document.getElementById(`OtpBuyNowBackdrop-${blockId}`);

      if (!this.triggerBtn) return;

      // Move backdrop to document.body root so parent container CSS filters / overflow do not blur or clip the modal
      if (this.backdrop && this.backdrop.parentElement !== document.body) {
        document.body.appendChild(this.backdrop);
      }

      // Extract initial metadata from button attributes
      this.productData = {
        productId: this.triggerBtn.dataset.productId || '',
        variantId: this.triggerBtn.dataset.variantId || '',
        productTitle: this.triggerBtn.dataset.productTitle || '',
        productPrice: parseFloat(this.triggerBtn.dataset.productPrice || '0'),
        loadingText: this.triggerBtn.dataset.loadingText || 'Processing...',
        email: this.triggerBtn.dataset.customerEmail || '',
        instamojoUrl: this.triggerBtn.dataset.instamojoUrl || 'https://imjo.in/XMHWfR',
        checkoutAction: this.triggerBtn.dataset.checkoutAction || 'direct'
      };

      this._pincodeDebounceTimer = null;
      this._lastFetchedPincode = '';

      this.initElements();
      this.bindEvents();
      this.listenVariantChanges();
    }

    initElements() {
      // Input Elements (only if modal backdrop exists)
      if (this.backdrop) {
        this.pincodeInput = document.getElementById(`OtpPincode-${this.blockId}`);
        this.pincodeError = document.getElementById(`OtpPincodeError-${this.blockId}`);
        this.pincodeBadge = document.getElementById(`OtpPincodeBadge-${this.blockId}`);
        this.pincodeSpinner = document.getElementById(`OtpPincodeSpinner-${this.blockId}`);

        this.cityInput = document.getElementById(`OtpCity-${this.blockId}`);
        this.cityError = document.getElementById(`OtpCityError-${this.blockId}`);

        this.stateSelect = document.getElementById(`OtpState-${this.blockId}`);
        this.stateError = document.getElementById(`OtpStateError-${this.blockId}`);

        this.addressInput = document.getElementById(`OtpAddress-${this.blockId}`);
        this.addressError = document.getElementById(`OtpAddressError-${this.blockId}`);

        // Submit Button inside Modal
        this.submitBtn = document.getElementById(`OtpStep4Submit-${this.blockId}`);

        // Modal Controls & Alerts
        this.closeBtn = this.backdrop.querySelector(`[data-otp-close-id="${this.blockId}"]`);
        this.alertEl = document.getElementById(`OtpAlert-${this.blockId}`);
      }
    }

    bindEvents() {
      // Trigger Buy Now Button Click
      this.triggerBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const action = this.triggerBtn.dataset.checkoutAction || this.productData.checkoutAction || 'direct';
        if (action === 'modal' && this.backdrop) {
          this.openModal();
        } else {
          this.handleDirectCheckout();
        }
      });

      // Restore button text on browser back navigation (bfcache)
      window.addEventListener('pageshow', () => {
        this.setTriggerButtonLoading(false);
      });

      // Modal Events (only if modal is configured and exists)
      if (this.backdrop) {
        // Close Modal Triggers
        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', () => this.closeModal());
        }
        this.backdrop.addEventListener('click', (e) => {
          if (e.target === this.backdrop) this.closeModal();
        });

        // ESC key to close
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.isModalOpen()) {
            this.closeModal();
          }
        });

        // Pincode Input (with auto-lookup)
        if (this.pincodeInput) {
          this.pincodeInput.addEventListener('input', (e) => this.handlePincodeInput(e));
        }

        // City Input
        if (this.cityInput) {
          this.cityInput.addEventListener('input', () => this.validateField('city'));
        }

        // State Dropdown
        if (this.stateSelect) {
          this.stateSelect.addEventListener('change', () => this.validateField('state'));
        }

        // Address Input
        if (this.addressInput) {
          this.addressInput.addEventListener('input', () => this.validateField('address'));
        }

        // Enter key triggers submit
        const inputs = [this.pincodeInput, this.cityInput, this.addressInput];
        inputs.forEach((input) => {
          if (input) {
            input.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                e.preventDefault();
                this.handleSubmit();
              }
            });
          }
        });

        // Modal Submit Button
        if (this.submitBtn) {
          this.submitBtn.addEventListener('click', () => this.handleSubmit());
        }
      }
    }

    /**
     * Set loading state on the main Buy Now trigger button
     */
    setTriggerButtonLoading(isLoading) {
      if (!this.triggerBtn) return;
      this.triggerBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      const textEl = this.triggerBtn.querySelector('.custom-buy-now-btn__text');
      const spinnerEl = this.triggerBtn.querySelector('.custom-buy-now-btn__spinner');

      if (spinnerEl) {
        spinnerEl.style.display = isLoading ? 'inline-block' : 'none';
      }

      if (isLoading) {
        this.triggerBtn.classList.add('is-loading');
        this.triggerBtn.disabled = true;
        this.triggerBtn.style.pointerEvents = 'none';
        if (textEl) {
          if (!this.triggerBtn._originalText) {
            this.triggerBtn._originalText = textEl.textContent.trim();
          }
          textEl.textContent = this.productData.loadingText || 'Processing...';
        }
      } else {
        this.triggerBtn.classList.remove('is-loading');
        this.triggerBtn.disabled = false;
        this.triggerBtn.style.pointerEvents = '';
        if (textEl && this.triggerBtn._originalText) {
          textEl.textContent = this.triggerBtn._originalText;
          this.triggerBtn._originalText = null;
        }
      }
    }

    /**
     * Get current active variant ID from DOM or URL
     */
    getCurrentVariantId() {
      const formInput = document.querySelector('form[action*="/cart/add"] input[name="id"], form[id^="product-form-"] input[name="id"], input[name="id"].product-variant-id, input[name="id"]');
      if (formInput && formInput.value) {
        return formInput.value;
      }
      const urlVariant = new URLSearchParams(window.location.search).get('variant');
      if (urlVariant) {
        return urlVariant;
      }
      return this.triggerBtn.dataset.variantId || this.productData.variantId || '';
    }

    /**
     * Get current active price from DOM
     */
    getCurrentPrice() {
      const priceSelectors = [
        '.product-detail__price .price',
        '.card-product-price .price',
        '[id^="price-"] .price',
        '.price-item--sale',
        '.price-item--regular',
        '.price__regular .price-item',
        '.product-info__price .price',
        '.product-price .price'
      ];
      for (let i = 0; i < priceSelectors.length; i++) {
        const el = document.querySelector(priceSelectors[i]);
        if (el) {
          const raw = el.innerText || el.textContent || '';
          const clean = raw.replace(/,/g, '');
          const match = clean.match(/\d+(?:\.\d+)?/);
          if (match && parseFloat(match[0]) > 0) {
            return parseFloat(match[0]);
          }
        }
      }
      return this.productData.productPrice || 399;
    }

    /**
     * Get variant title for order storage
     */
    getCurrentVariantTitle() {
      const varTitleEl = document.getElementById(`OtpVariantTitle-${this.blockId}`);
      if (varTitleEl && varTitleEl.textContent.trim()) {
        return varTitleEl.textContent.trim();
      }
      const checkedRadios = document.querySelectorAll('input[type="radio"]:checked[name^="options"], input[type="radio"]:checked[name="Size"], input[type="radio"]:checked[name="Color"]');
      if (checkedRadios.length > 0) {
        return Array.from(checkedRadios).map(r => r.value).join(' / ');
      }
      return '';
    }

    /**
     * Direct Instant Checkout -> Bypass Modal & Navigate directly to Instamojo
     */
    handleDirectCheckout() {
      if (this.triggerBtn.classList.contains('is-loading')) return;

      this.setTriggerButtonLoading(true);

      const currentVariantId = this.getCurrentVariantId();
      const currentPrice = this.getCurrentPrice();
      const currentVariantTitle = this.getCurrentVariantTitle();
      const targetAmount = String(Math.floor(currentPrice || this.productData.productPrice || 399));
      const baseLink = (this.triggerBtn.dataset.instamojoUrl || this.productData.instamojoUrl || 'https://imjo.in/XMHWfR').trim();

      // Store pending order details in localStorage for return confirmation & console logging
      const pendingOrder = {
        orderReference: 'ORD-' + Date.now(),
        timestamp: new Date().toISOString(),
        product: {
          productId: this.productData.productId,
          variantId: currentVariantId,
          title: this.productData.productTitle,
          variantTitle: currentVariantTitle,
          fullPrice: currentPrice
        },
        payment: {
          mode: 'Prepaid (Direct Instamojo)',
          amountToPay: targetAmount,
          gateway: 'Instamojo',
          targetUrl: baseLink
        },
        customer: {
          email: this.productData.email || '',
          fullShippingAddress: 'Provided via Instamojo checkout'
        },
        status: 'PENDING_PAYMENT'
      };

      try {
        localStorage.setItem('cordsetzone_pending_order', JSON.stringify(pendingOrder));
        localStorage.setItem('last_custom_checkout_order', JSON.stringify(pendingOrder));
      } catch (e) {
        console.warn('Could not save pending order to storage:', e);
      }

      console.log('📦 [CORDSETZONE] Direct Checkout Initiated:', pendingOrder);

      // Build target Instamojo URL with pre-filled amount
      let instamojoUrl = baseLink;
      if (!instamojoUrl.startsWith('http://') && !instamojoUrl.startsWith('https://')) {
        instamojoUrl = `https://${instamojoUrl}`;
      }

      try {
        const urlObj = new URL(instamojoUrl);

        if (targetAmount && parseFloat(targetAmount) > 0) {
          urlObj.searchParams.set('amount', targetAmount);
          urlObj.searchParams.set('total_amount', targetAmount);
          urlObj.searchParams.set('data_amount', targetAmount);
        }

        if (this.productData.email) {
          urlObj.searchParams.set('email', this.productData.email);
          urlObj.searchParams.set('data_email', this.productData.email);
        }

        console.log('🚀 [CORDSETZONE] Redirecting directly to Instamojo URL:', urlObj.toString());
        window.location.href = urlObj.toString();
      } catch (err) {
        console.error('URL construction error, using fallback:', err);
        const separator = instamojoUrl.includes('?') ? '&' : '?';
        const fallbackUrl = (targetAmount && parseFloat(targetAmount) > 0)
          ? `${instamojoUrl}${separator}amount=${encodeURIComponent(targetAmount)}`
          : instamojoUrl;
        window.location.href = fallbackUrl;
      }
    }

    /**
     * Handle variant changes on Shopify product page
     */
    listenVariantChanges() {
      const updateVariantData = (variantId, price, title) => {
        if (variantId) {
          this.productData.variantId = variantId;
          this.triggerBtn.dataset.variantId = variantId;
        }
        if (price) {
          this.productData.productPrice = parseFloat(price);
          this.triggerBtn.dataset.productPrice = price;
          const priceEl = document.getElementById(`OtpProductPrice-${this.blockId}`);
          if (priceEl) {
            priceEl.textContent = window.Shopify && Shopify.formatMoney ? Shopify.formatMoney(price * 100) : `₹${Math.floor(price)}`;
          }

          if (this.submitBtn) {
            const btnText = this.submitBtn.querySelector('.otp-btn-text');
            if (btnText) btnText.textContent = `Pay ₹${Math.floor(price)}`;
          }
        }
        if (title) {
          const varTitleEl = document.getElementById(`OtpVariantTitle-${this.blockId}`);
          if (varTitleEl) varTitleEl.textContent = title;
        }
      };

      // Listen for Shopify variant change custom events
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.variant) {
          updateVariantData(e.detail.variant.id, e.detail.variant.price / 100, e.detail.variant.title);
        }
      });

      // Listen to form input changes inside product forms
      document.addEventListener('change', (e) => {
        if (e.target && (e.target.name === 'id' || e.target.closest('form[action*="/cart/add"]'))) {
          const variantInput = document.querySelector('form[action*="/cart/add"] input[name="id"], input[name="id"]');
          if (variantInput && variantInput.value) {
            this.productData.variantId = variantInput.value;
            this.triggerBtn.dataset.variantId = variantInput.value;
          }
          setTimeout(() => {
            const curPrice = this.getCurrentPrice();
            if (curPrice) {
              this.productData.productPrice = curPrice;
              this.triggerBtn.dataset.productPrice = curPrice;
            }
          }, 100);
        }
      });

      // Listen for swatch clicks
      document.addEventListener('click', (e) => {
        if (e.target && e.target.closest('.product__color-swatches--js, .product__item-option, [data-value], swatch-dropdown-item')) {
          setTimeout(() => {
            const variantInput = document.querySelector('form[action*="/cart/add"] input[name="id"], input[name="id"]');
            if (variantInput && variantInput.value) {
              this.productData.variantId = variantInput.value;
              this.triggerBtn.dataset.variantId = variantInput.value;
            }
            const curPrice = this.getCurrentPrice();
            if (curPrice) {
              this.productData.productPrice = curPrice;
              this.triggerBtn.dataset.productPrice = curPrice;
            }
          }, 150);
        }
      });
    }

    openModal() {
      this.clearAlert();
      this.backdrop.style.display = 'flex';
      void this.backdrop.offsetWidth; // Force reflow
      this.backdrop.classList.add('otp-modal-active');
      document.body.classList.add('otp-modal-open');

      // Autofill from previous session if available
      try {
        const lastOrder = localStorage.getItem('last_custom_checkout_order') || localStorage.getItem('cordsetzone_pending_order');
        if (lastOrder) {
          const data = JSON.parse(lastOrder);
          if (data && data.customer) {
            if (this.pincodeInput && !this.pincodeInput.value) {
              this.pincodeInput.value = data.customer.pincode || '';
              if (data.customer.pincode && data.customer.pincode.length === 6) {
                this.fetchPincodeDetails(data.customer.pincode);
              }
            }
            if (this.cityInput && !this.cityInput.value) this.cityInput.value = data.customer.city || '';
            if (this.stateSelect && !this.stateSelect.value) this.stateSelect.value = data.customer.state || '';
            if (this.addressInput && !this.addressInput.value) this.addressInput.value = data.customer.address || '';
          }
        }
      } catch (e) {}

      setTimeout(() => {
        if (this.pincodeInput && !this.pincodeInput.value) {
          this.pincodeInput.focus();
        } else if (this.addressInput && !this.addressInput.value) {
          this.addressInput.focus();
        }
      }, 150);
    }

    closeModal() {
      this.backdrop.classList.remove('otp-modal-active');
      document.body.classList.remove('otp-modal-open');

      setTimeout(() => {
        this.backdrop.style.display = 'none';
      }, 250);
    }

    isModalOpen() {
      return this.backdrop.classList.contains('otp-modal-active');
    }

    showAlert(message, type = 'error') {
      if (!this.alertEl) return;
      this.alertEl.textContent = message;
      this.alertEl.className = `otp-alert otp-alert-${type}`;
      this.alertEl.style.display = 'block';
    }

    clearAlert() {
      if (!this.alertEl) return;
      this.alertEl.style.display = 'none';
      this.alertEl.textContent = '';
    }

    /* -------------------------------------------------------------------------- */
    /* PINCODE AUTO-LOOKUP & VALIDATION                                           */
    /* -------------------------------------------------------------------------- */
    handlePincodeInput(e) {
      let rawVal = e.target.value.replace(/\D/g, '');
      if (rawVal.length > 6) rawVal = rawVal.slice(0, 6);
      e.target.value = rawVal;

      this.validateField('pincode');

      if (rawVal.length === 6) {
        if (this._lastFetchedPincode !== rawVal) {
          clearTimeout(this._pincodeDebounceTimer);
          this._pincodeDebounceTimer = setTimeout(() => {
            this.fetchPincodeDetails(rawVal);
          }, 300);
        }
      } else {
        if (this.pincodeBadge) this.pincodeBadge.style.display = 'none';
      }
    }

    /**
     * Auto-lookup City and State via Postal Pincode API
     */
    async fetchPincodeDetails(pincode) {
      if (this.pincodeSpinner) this.pincodeSpinner.style.display = 'inline-block';
      if (this.pincodeBadge) this.pincodeBadge.style.display = 'none';
      this._lastFetchedPincode = pincode;

      try {
        const url = `${API_CONFIG.PINCODE_LOOKUP}${pincode}`;
        const data = await fetchWithTimeout(url, {}, 6000);

        if (Array.isArray(data) && data[0] && data[0].Status === 'Success' && Array.isArray(data[0].PostOffice) && data[0].PostOffice.length > 0) {
          const po = data[0].PostOffice[0];
          const district = po.District || po.Block || po.Circle || '';
          const stateName = po.State || '';

          // Populate City if empty or previously auto-filled
          if (this.cityInput && (!this.cityInput.value || this.cityInput.dataset.autoFilled === 'true')) {
            this.cityInput.value = district;
            this.cityInput.dataset.autoFilled = 'true';
            this.validateField('city');
          }

          // Match and select State in dropdown
          if (this.stateSelect && stateName) {
            let matched = false;
            const normalizedState = stateName.trim().toLowerCase();

            for (let i = 0; i < this.stateSelect.options.length; i++) {
              const opt = this.stateSelect.options[i];
              if (opt.value.trim().toLowerCase() === normalizedState || opt.text.trim().toLowerCase() === normalizedState) {
                this.stateSelect.selectedIndex = i;
                matched = true;
                break;
              }
            }

            // Partial match fallback if exact match not found
            if (!matched) {
              for (let i = 0; i < this.stateSelect.options.length; i++) {
                const opt = this.stateSelect.options[i];
                if (opt.value && (normalizedState.includes(opt.value.toLowerCase()) || opt.value.toLowerCase().includes(normalizedState))) {
                  this.stateSelect.selectedIndex = i;
                  matched = true;
                  break;
                }
              }
            }

            this.validateField('state');
          }

          // Show green success badge
          if (this.pincodeBadge) {
            this.pincodeBadge.textContent = `✓ ${district ? district + ', ' : ''}${stateName}`;
            this.pincodeBadge.style.display = 'inline-flex';
          }
        } else {
          if (this.pincodeBadge) {
            this.pincodeBadge.textContent = 'Please enter City & State';
            this.pincodeBadge.style.color = '#6b7280';
            this.pincodeBadge.style.display = 'inline-flex';
          }
        }
      } catch (err) {
        console.warn('Pincode lookup failed:', err);
      } finally {
        if (this.pincodeSpinner) this.pincodeSpinner.style.display = 'none';
      }
    }

    validateField(fieldName) {
      let isValid = true;

      if (fieldName === 'pincode' || fieldName === 'all') {
        const val = this.pincodeInput ? this.pincodeInput.value.trim() : '';
        if (val.length !== 6) {
          if (this.pincodeError) this.pincodeError.textContent = 'Please enter a valid 6-digit pincode.';
          isValid = false;
        } else {
          if (this.pincodeError) this.pincodeError.textContent = '';
        }
      }

      if (fieldName === 'city' || fieldName === 'all') {
        const val = this.cityInput ? this.cityInput.value.trim() : '';
        if (val.length < 2) {
          if (this.cityError) this.cityError.textContent = 'Please enter your city / district.';
          isValid = false;
        } else {
          if (this.cityError) this.cityError.textContent = '';
        }
      }

      if (fieldName === 'state' || fieldName === 'all') {
        const val = this.stateSelect ? this.stateSelect.value.trim() : '';
        if (!val) {
          if (this.stateError) this.stateError.textContent = 'Please select your state.';
          isValid = false;
        } else {
          if (this.stateError) this.stateError.textContent = '';
        }
      }

      if (fieldName === 'address' || fieldName === 'all') {
        const val = this.addressInput ? this.addressInput.value.trim() : '';
        if (val.length < 5) {
          if (this.addressError) this.addressError.textContent = 'Please enter your complete address (at least 5 characters).';
          isValid = false;
        } else {
          if (this.addressError) this.addressError.textContent = '';
        }
      }

      return isValid;
    }

    validateAll() {
      const isPincodeValid = this.validateField('pincode');
      const isCityValid = this.validateField('city');
      const isStateValid = this.validateField('state');
      const isAddressValid = this.validateField('address');

      return isPincodeValid && isCityValid && isStateValid && isAddressValid;
    }

    setButtonLoading(isLoading) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = isLoading;
      const textEl = this.submitBtn.querySelector('.otp-btn-text');
      const spinnerEl = this.submitBtn.querySelector('.otp-spinner');

      if (spinnerEl) spinnerEl.style.display = isLoading ? 'inline-block' : 'none';

      if (isLoading && textEl) {
        if (!this.submitBtn._originalText) this.submitBtn._originalText = textEl.textContent;
        textEl.textContent = this.productData.loadingText || 'Redirecting to Instamojo...';
      } else if (!isLoading && textEl && this.submitBtn._originalText) {
        textEl.textContent = this.submitBtn._originalText;
        this.submitBtn._originalText = null;
      }
    }

    /* -------------------------------------------------------------------------- */
    /* SUBMIT & REDIRECT TO INSTAMOJO                                             */
    /* -------------------------------------------------------------------------- */
    async handleSubmit() {
      this.clearAlert();

      if (!this.validateAll()) {
        this.showAlert('Please fill in all required delivery details correctly.');
        const firstErrorField = this.backdrop.querySelector('.otp-field-error:not(:empty)');
        if (firstErrorField && firstErrorField.previousElementSibling) {
          const inputEl = firstErrorField.previousElementSibling.querySelector('input, select, textarea') || firstErrorField.previousElementSibling;
          if (inputEl && typeof inputEl.focus === 'function') inputEl.focus();
        }
        return;
      }

      this.setButtonLoading(true);

      const pincode = this.pincodeInput ? this.pincodeInput.value.trim() : '';
      const city = this.cityInput ? this.cityInput.value.trim() : '';
      const state = this.stateSelect ? this.stateSelect.value.trim() : '';
      const address = this.addressInput ? this.addressInput.value.trim() : '';
      const targetAmount = String(Math.floor(this.productData.productPrice || 499));
      const baseLink = this.productData.instamojoUrl || 'https://imjo.in/XMHWfR';

      const variantTitleEl = document.getElementById(`OtpVariantTitle-${this.blockId}`);
      const variantTitle = variantTitleEl ? variantTitleEl.textContent.trim() : '';

      // Save pending order object in localStorage
      const pendingOrder = {
        orderReference: 'ORD-' + Date.now(),
        timestamp: new Date().toISOString(),
        product: {
          productId: this.productData.productId,
          variantId: this.productData.variantId,
          title: this.productData.productTitle,
          variantTitle: variantTitle,
          fullPrice: this.productData.productPrice
        },
        payment: {
          mode: 'Prepaid (Full Payment)',
          amountToPay: targetAmount,
          gateway: 'Instamojo',
          targetUrl: baseLink
        },
        customer: {
          address: address,
          city: city,
          state: state,
          pincode: pincode,
          fullShippingAddress: `${address}, ${city}, ${state} - ${pincode}`
        },
        status: 'PENDING_PAYMENT'
      };

      try {
        localStorage.setItem('cordsetzone_pending_order', JSON.stringify(pendingOrder));
        localStorage.setItem('last_custom_checkout_order', JSON.stringify(pendingOrder));
      } catch (e) {
        console.warn('Could not save pending order to localStorage:', e);
      }

      console.log('📦 [CORDSETZONE] Pending Order Saved to Storage:', pendingOrder);

      // Build target Instamojo URL with pre-filled parameters
      let instamojoUrl = baseLink.trim();
      if (!instamojoUrl.startsWith('http://') && !instamojoUrl.startsWith('https://')) {
        instamojoUrl = `https://${instamojoUrl}`;
      }

      try {
        const urlObj = new URL(instamojoUrl);

        // Pre-fill Amount
        if (targetAmount) {
          urlObj.searchParams.set('amount', targetAmount);
          urlObj.searchParams.set('total_amount', targetAmount);
          urlObj.searchParams.set('data_amount', targetAmount);
        }

        // Pre-fill Address & Location
        const fullStreet = city ? `${address}, ${city}` : address;
        urlObj.searchParams.set('billing_address_street', fullStreet);
        urlObj.searchParams.set('address', fullStreet);

        if (city) {
          urlObj.searchParams.set('billing_address_city', city);
          urlObj.searchParams.set('city', city);
        }
        if (state) {
          urlObj.searchParams.set('billing_address_state', state);
          urlObj.searchParams.set('state', state);
        }
        if (pincode) {
          urlObj.searchParams.set('billing_address_zip', pincode);
          urlObj.searchParams.set('pincode', pincode);
          urlObj.searchParams.set('zipcode', pincode);
        }

        console.log('🚀 Redirecting to pre-filled Instamojo URL:', urlObj.toString());
        window.location.href = urlObj.toString();
      } catch (err) {
        console.error('URL construction error, using fallback format:', err);
        const separator = instamojoUrl.includes('?') ? '&' : '?';
        const fallbackUrl = `${instamojoUrl}${separator}amount=${encodeURIComponent(targetAmount)}&billing_address_street=${encodeURIComponent(address)}&billing_address_city=${encodeURIComponent(city)}&billing_address_state=${encodeURIComponent(state)}&billing_address_zip=${encodeURIComponent(pincode)}`;
        window.location.href = fallbackUrl;
      }
    }
  }

  /**
   * Order Confirmation Modal & Console Logger for Return Visits
   */
  function handlePostPaymentReturn() {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('payment_id') || urlParams.get('payment_request_id');
    const paymentStatus = urlParams.get('payment_status') || (urlParams.get('payment') === 'success' ? 'Credit' : '');
    const isSuccess = urlParams.get('payment') === 'success' || urlParams.get('status') === 'success' || paymentStatus === 'Credit' || !!paymentId;

    if (!isSuccess) return;

    // Retrieve pending order details from storage
    let orderData = null;
    try {
      const stored = localStorage.getItem('cordsetzone_pending_order') || localStorage.getItem('last_custom_checkout_order');
      if (stored) {
        orderData = JSON.parse(stored);
      }
    } catch (e) {}

    // Complete order record
    const confirmedOrder = {
      orderId: (orderData && orderData.orderReference) || 'ORD-' + Date.now(),
      status: paymentStatus === 'Failed' ? 'PAYMENT_FAILED' : 'ORDER_PLACED_CONFIRMED',
      paymentDetails: {
        gateway: 'Instamojo',
        paymentId: paymentId || 'OFFLINE_OR_DIRECT',
        paymentStatus: paymentStatus || 'Credit',
        paymentRequestId: urlParams.get('payment_request_id') || ''
      },
      product: (orderData && orderData.product) || {
        title: document.title,
        price: 'N/A'
      },
      customer: (orderData && orderData.customer) || {
        name: urlParams.get('buyer_name') || 'Customer',
        phone: urlParams.get('phone') || '',
        email: urlParams.get('email') || ''
      },
      paymentMode: (orderData && orderData.payment && orderData.payment.mode) || 'Prepaid',
      amountPaid: (orderData && orderData.payment && orderData.payment.amountToPay) || urlParams.get('amount') || 'Paid',
      confirmedAt: new Date().toISOString()
    };

    // Save final order in storage
    try {
      localStorage.setItem('cordsetzone_last_completed_order', JSON.stringify(confirmedOrder));
    } catch (e) {}

    // Print all details to console
    console.log('===========================================================');
    console.log('🎉 [CORDSETZONE] ORDER RECORD CREATED & PAYMENT VERIFIED:');
    console.log(JSON.stringify(confirmedOrder, null, 2));
    console.log('===========================================================');

    // Display rich Confirmation Modal
    showOrderSuccessModal(confirmedOrder);
  }

  /**
   * Render Premium Glassmorphic Confirmation Modal
   */
  function showOrderSuccessModal(order) {
    const backdrop = document.createElement('div');
    backdrop.className = 'otp-modal-backdrop otp-modal-active';
    backdrop.style.display = 'flex';
    backdrop.style.justifyContent = 'center';
    backdrop.style.alignItems = 'center';
    backdrop.style.zIndex = '100000';

    const wrapper = document.createElement('div');
    wrapper.className = 'otp-modal-wrapper';
    wrapper.style.maxWidth = '460px';
    wrapper.style.textAlign = 'center';
    wrapper.style.padding = '30px 24px';
    wrapper.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.25)';

    wrapper.innerHTML = `
      <div style="margin-bottom: 16px;">
        <div style="display: inline-flex; align-items: center; justify-content: center; width: 72px; height: 72px; background-color: #ECFDF5; border-radius: 50%; border: 3px solid #A7F3D0;">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
      </div>
      <h2 style="font-size: 22px; color: #111827; margin: 0 0 6px 0; font-weight: 700;">Order Confirmed!</h2>
      <p style="font-size: 14px; color: #4B5563; margin: 0 0 18px 0; line-height: 1.5;">
        Thank you for your purchase! Your order <strong>#${order.orderId}</strong> has been received and is being processed.
      </p>

      <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin-bottom: 20px; text-align: left; font-size: 13px; color: #334155; line-height: 1.6;">
        <div style="display: flex; justify-content: space-between; border-bottom: 1px dashed #cbd5e1; padding-bottom: 6px; margin-bottom: 6px;">
          <span style="color: #64748b;">Product:</span>
          <strong style="color: #0f172a; max-width: 60%; text-align: right; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${order.product.title}</strong>
        </div>
        <div style="display: flex; justify-content: space-between; border-bottom: 1px dashed #cbd5e1; padding-bottom: 6px; margin-bottom: 6px;">
          <span style="color: #64748b;">Payment Method:</span>
          <strong style="color: #059669;">${order.paymentMode}</strong>
        </div>
        <div style="display: flex; justify-content: space-between; border-bottom: 1px dashed #cbd5e1; padding-bottom: 6px; margin-bottom: 6px;">
          <span style="color: #64748b;">Amount Paid:</span>
          <strong style="color: #0f172a;">₹${order.amountPaid}</strong>
        </div>
        <div style="margin-top: 6px;">
          <span style="color: #64748b;">Shipping To:</span>
          <div style="font-weight: 600; color: #1e293b; margin-top: 2px;">${(order.customer && (order.customer.fullShippingAddress || order.customer.address)) || 'N/A'}</div>
        </div>
      </div>

      <button type="button" class="otp-btn otp-btn-primary" id="SuccessCloseBtn" style="margin: 0; width: 100%;">Continue Shopping</button>
    `;

    backdrop.appendChild(wrapper);
    document.body.appendChild(backdrop);
    document.body.classList.add('otp-modal-open');

    const closeBtn = wrapper.querySelector('#SuccessCloseBtn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        backdrop.remove();
        document.body.classList.remove('otp-modal-open');
        const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
        window.history.replaceState({ path: cleanUrl }, '', cleanUrl);
      });
    }
  }

  // Initialize controllers for all Custom Buy Now blocks on page load
  function initOTPBlocks() {
    handlePostPaymentReturn();

    const triggerButtons = document.querySelectorAll('.custom-buy-now-btn');
    triggerButtons.forEach((btn) => {
      const blockId = btn.dataset.blockId;
      if (blockId && !btn._otpControllerInstance) {
        btn._otpControllerInstance = new OTPBuyNowController(blockId);
      }
    });

    const cardButtons = document.querySelectorAll('.custom-card-buy-now-btn');
    cardButtons.forEach((cardBtn) => {
      if (!cardBtn._boundDirectCheckout) {
        cardBtn._boundDirectCheckout = true;
        cardBtn.addEventListener('click', function () {
          const spinner = cardBtn.querySelector('.custom-card-btn__spinner');
          const icons = cardBtn.querySelectorAll('.hidden-on-load');
          if (spinner) {
            spinner.style.display = 'inline-block';
            spinner.classList.remove('opacity-0');
          }
          icons.forEach((ic) => {
            ic.style.display = 'none';
          });
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOTPBlocks);
  } else {
    initOTPBlocks();
  }

  // Handle dynamic injection (e.g., Quick View modals)
  if (window.MutationObserver) {
    const observer = new MutationObserver(function (mutations) {
      let shouldInit = false;
      for (let i = 0; i < mutations.length; i++) {
        const addedNodes = mutations[i].addedNodes;
        for (let j = 0; j < addedNodes.length; j++) {
          const node = addedNodes[j];
          if (node.nodeType === 1 && (node.classList?.contains('custom-buy-now-btn') || node.querySelector?.('.custom-buy-now-btn'))) {
            shouldInit = true;
            break;
          }
        }
        if (shouldInit) break;
      }
      if (shouldInit) {
        initOTPBlocks();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Section lifecycle event hooks
  document.addEventListener('shopify:section:load', initOTPBlocks);
  document.addEventListener('shopify:section:reorder', initOTPBlocks);
  document.addEventListener('shopify:block:select', initOTPBlocks);
})();
